import { Formik } from 'formik';
import React from 'react';
import { Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as Yup from 'yup';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Input from '../../components/FormFields/Input';
import Modal from '../../components/Modal';
import { Header3, P2 } from '../../components/Typography';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  UPDATE_ORGANIZATION_NAME_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_NAME_STORE_KEY,
  updateOrganizationNameRequested,
} from '../../redux/organization/organization.actions';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../FlashMessage';

// The same as `organization.constants.ts` in tenancy
const ORGANIZATION_NAME_MAX_LENGTH = 120;

interface EditOrganizationNameModalProps {
  organizationId: string;
  name: string;
  onDismiss: () => void;
}

interface EditOrganizationNameFormValues {
  name: string;
}

const Actions = styled(Row)`
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;
  padding-top: 24px;
`;

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required('Required')
    .max(ORGANIZATION_NAME_MAX_LENGTH, `Organisation name must be ${ORGANIZATION_NAME_MAX_LENGTH} characters or fewer`),
});

const EditOrganizationNameModal: React.FC<EditOrganizationNameModalProps> = ({ name, organizationId, onDismiss }) => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector(createLoadingSelector([UPDATE_ORGANIZATION_NAME_STORE_KEY]));
  const [flashMessage, onDismissFlashMessage] = useFlashMessage([UPDATE_ORGANIZATION_NAME_MODAL_STORE_KEY]);
  const handleDismiss = () => {
    onDismissFlashMessage();
    onDismiss();
  };

  return (
    <Modal onDismiss={handleDismiss}>
      {flashMessage ? (
        <FlashMessage payload={flashMessage} onDismiss={onDismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
      ) : null}
      <Header3 style={{ marginBottom: 6 }}>Edit Organisation Name</Header3>
      <P2>
        <span style={{ fontWeight: 'bold' }}>Important Notice:</span> This name is what tenants and agents may see in future
        emails and the apps they use.
      </P2>
      <Formik<EditOrganizationNameFormValues>
        initialValues={{ name }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onDismissFlashMessage();
          dispatch(
            updateOrganizationNameRequested({
              organizationId,
              name: values.name.trim(),
            }),
          );
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <FieldWithLabel label="Organisation name" error={errors.name} touched={touched.name}>
              <Input
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.name}
                touched={touched.name}
              />
            </FieldWithLabel>
            <Container fluid>
              <Actions>
                <Button buttonType="secondary" noArrow={true} onClick={handleDismiss} type="button">
                  Cancel
                </Button>
                <Button disabled={isSubmitting} type="submit">
                  Save
                </Button>
              </Actions>
            </Container>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditOrganizationNameModal;
